import React, { useState, useEffect } from 'react';
import { getApiClient } from 'utils/apiClient';
import { useTranslation } from 'utils/i18n';
import { useAuthState } from 'hooks/useAuthState';
import { logger } from 'utils/logger';
import translations from './translations';
import caseStudiesTranslations from './case_studies/translations';
import partnersTranslations from './partners/translations';
import integrations from './data/integrations';
import ProofHeader from './ProofHeader';
import ProofMetrics from './ProofMetrics';
import ProofTabNavigation from './ProofTabNavigation';
import SearchFilterControls from './SearchFilterControls';
import CaseStudiesPanel from './CaseStudiesPanel';
import PartnersPanel from './PartnersPanel';
import TechStackPanel from './TechStackPanel';
import IntegrationsPanel from './IntegrationsPanel';
import './ProofContainer.css';

const ProofContainer = ({ hideTechStack = false, hideIntegrationsTab = false }) => {
  const { language } = useTranslation();
  const { isAuthenticated } = useAuthState();
  
  // Get component-specific translations - memoized to prevent unnecessary recalculations
  const proofT = React.useCallback((key, defaultValue = '') => {
    try {
      if (!translations || typeof translations !== 'object') {
        console.warn('ProofContainer: translations object is missing or invalid', { translations });
        return defaultValue !== '' ? defaultValue : key;
      }
      
      // Normalize language code to lowercase
      const normalizedLang = (language || 'en').toLowerCase();
      
      // Debug: Log available translation keys
      const availableLangs = Object.keys(translations);
      if (!availableLangs.includes(normalizedLang) && !availableLangs.includes('en')) {
        console.warn(`ProofContainer: Language "${normalizedLang}" not found. Available:`, availableLangs);
      }
      
      // Get translations for current language, fallback to English
      const componentTranslations = translations[normalizedLang] || translations.en || translations['en'];
      
      if (!componentTranslations || typeof componentTranslations !== 'object') {
        console.warn(`ProofContainer: No translations found for language "${normalizedLang}" or "en"`, {
          normalizedLang,
          hasNormalizedLang: !!translations[normalizedLang],
          hasEn: !!translations.en,
          availableLangs
        });
        return defaultValue !== '' ? defaultValue : key;
      }
      
      // Navigate through nested keys (e.g., "caseStudies.loading" -> componentTranslations.caseStudies.loading)
      const value = key.split('.').reduce((obj, k) => {
        if (obj && typeof obj === 'object') {
          return obj[k];
        }
        return undefined;
      }, componentTranslations);
      
      // Return the value if found, otherwise use default or key
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
      
      // If value not found, log for debugging
      if (process.env.NODE_ENV === 'development') {
        console.warn(`ProofContainer: Translation key "${key}" not found for language "${normalizedLang}"`, {
          key,
          normalizedLang,
          availableKeys: Object.keys(componentTranslations).slice(0, 10) // First 10 keys for debugging
        });
      }
      
      return defaultValue !== '' ? defaultValue : key;
    } catch (error) {
      console.error('ProofContainer: Error in proofT function:', error, { key, language, defaultValue });
      return defaultValue !== '' ? defaultValue : key;
    }
  }, [language]); // Re-create function when language changes
  
  const [activeTab, setActiveTab] = useState('case-studies');

  useEffect(() => {
    if (hideIntegrationsTab && activeTab === 'integrations') {
      setActiveTab('case-studies');
    }
  }, [hideIntegrationsTab, activeTab]);
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [animatedValues, setAnimatedValues] = useState({
    totalInteractions: 0,
    avgROI: 0,
    costReduction: 0,
    responseTime: 0
  });
  const [sessionStats, setSessionStats] = useState({
    count: 0,
    displayText: "0",
    isLoading: true
  });
  const [slaResponseTime, setSlaResponseTime] = useState(null); // Store SLA response time in seconds

  // Case studies data - loaded from external JSON
  const [caseStudies, setCaseStudies] = useState([]);
  const [caseStudiesLoading, setCaseStudiesLoading] = useState(true);

  // Partners data - loaded from external JSON
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(true);

  // Load case studies and partners data - language-aware from local translations
  useEffect(() => {
    const loadData = async () => {
      try {
        const normalizedLang = (language || 'en').toLowerCase();
        
        // Load case studies from local translations first, then try API and static files as fallback
        let caseStudiesData = null;
        let hasLocalData = false;
        
        // Try local translations first - only use if they have actual data
        if (caseStudiesTranslations && typeof caseStudiesTranslations === 'object') {
          const langTranslations = caseStudiesTranslations[normalizedLang] || caseStudiesTranslations.en || caseStudiesTranslations['en'];
          if (langTranslations && langTranslations.caseStudies && Array.isArray(langTranslations.caseStudies) && langTranslations.caseStudies.length > 0) {
            caseStudiesData = langTranslations.caseStudies;
            hasLocalData = true;
          }
        }
        
        // If no local data or local data is empty, try static files as fallback
        if (!hasLocalData) {
            console.log(`[ProofContainer] Trying static file fallbacks...`);
            const staticPaths = [
              `/docs/resources/case_studies/index.${normalizedLang}.json`,
              `/docs/resources/case_studies/${normalizedLang}.json`,
              '/docs/resources/case_studies/index.en.json',
              '/docs/resources/case_studies/index.json'
            ];
            
            for (const path of staticPaths) {
              try {
                console.log(`[ProofContainer] Attempting to load case studies from: ${path}`);
                const response = await fetch(path);
                if (response.ok) {
                  const staticData = await response.json();
                  if (staticData && staticData.caseStudies && Array.isArray(staticData.caseStudies) && staticData.caseStudies.length > 0) {
                    console.log(`[ProofContainer] Successfully loaded ${staticData.caseStudies.length} case studies from: ${path}`);
                    caseStudiesData = staticData.caseStudies;
                    break;
                  } else if (Array.isArray(staticData) && staticData.length > 0) {
                    console.log(`[ProofContainer] Successfully loaded ${staticData.length} case studies (array format) from: ${path}`);
                    caseStudiesData = staticData;
                    break;
                  } else {
                    console.warn(`[ProofContainer] File ${path} loaded but contains no valid case studies data`);
                  }
                } else {
                  console.warn(`[ProofContainer] Failed to load ${path}: HTTP ${response.status}`);
                }
              } catch (e) {
                console.warn(`[ProofContainer] Error loading case studies from ${path}:`, e.message);
                continue;
              }
            }
            
            if (!caseStudiesData || (Array.isArray(caseStudiesData) && caseStudiesData.length === 0)) {
              console.warn(`[ProofContainer] No case studies data found for language "${normalizedLang}" from any source (local translations or static files)`);
            }
        } else {
          console.log(`[ProofContainer] Using local case studies data for language "${normalizedLang}" (${caseStudiesData.length} items)`);
        }
        
        // Set case studies data
        if (caseStudiesData && Array.isArray(caseStudiesData) && caseStudiesData.length > 0) {
          setCaseStudies(caseStudiesData);
        } else {
          setCaseStudies([]);
        }
        setCaseStudiesLoading(false);

        // Load partners from local translations first, then try API and static files as fallback
        let partnersData = null;
        let hasLocalPartnersData = false;
        
        // Try local translations first - only use if they have actual data
        if (partnersTranslations && typeof partnersTranslations === 'object') {
          const langTranslations = partnersTranslations[normalizedLang] || partnersTranslations.en || partnersTranslations['en'];
          if (langTranslations && langTranslations.partners && Array.isArray(langTranslations.partners) && langTranslations.partners.length > 0) {
            partnersData = langTranslations.partners;
            hasLocalPartnersData = true;
          }
        }
        
        // If no local data or local data is empty, try static files as fallback
        if (!hasLocalPartnersData) {
            console.log(`[ProofContainer] Trying static file fallbacks...`);
            const staticPaths = [
              `/docs/resources/partners/index.${normalizedLang}.json`,
              `/docs/resources/partners/${normalizedLang}.json`,
              '/docs/resources/partners/index.en.json',
              '/docs/resources/partners/index.json'
            ];
            
            for (const path of staticPaths) {
              try {
                console.log(`[ProofContainer] Attempting to load partners from: ${path}`);
                const response = await fetch(path);
                if (response.ok) {
                  const staticData = await response.json();
                  if (staticData && staticData.partners && Array.isArray(staticData.partners) && staticData.partners.length > 0) {
                    console.log(`[ProofContainer] Successfully loaded ${staticData.partners.length} partners from: ${path}`);
                    partnersData = staticData.partners;
                    break;
                  } else if (Array.isArray(staticData) && staticData.length > 0) {
                    console.log(`[ProofContainer] Successfully loaded ${staticData.length} partners (array format) from: ${path}`);
                    partnersData = staticData;
                    break;
                  } else {
                    console.warn(`[ProofContainer] File ${path} loaded but contains no valid partners data`);
                  }
                } else {
                  console.warn(`[ProofContainer] Failed to load ${path}: HTTP ${response.status}`);
                }
              } catch (e) {
                console.warn(`[ProofContainer] Error loading partners from ${path}:`, e.message);
                continue;
              }
            }
            
            if (!partnersData || (Array.isArray(partnersData) && partnersData.length === 0)) {
              console.warn(`[ProofContainer] No partners data found for language "${normalizedLang}" from any source (local translations or static files)`);
            }
        } else {
          console.log(`[ProofContainer] Using local partners data for language "${normalizedLang}" (${partnersData.length} items)`);
        }
        
        // Set partners data
        if (partnersData && Array.isArray(partnersData) && partnersData.length > 0) {
          setPartners(partnersData);
        } else {
          setPartners([]);
        }
        setPartnersLoading(false);
      } catch (error) {
        console.warn('Failed to load case studies or partners data:', error);
        setCaseStudiesLoading(false);
        setPartnersLoading(false);
      }
    };

    loadData();
  }, [language]); // Reload when language changes

  // Fetch session statistics
  useEffect(() => {
    const fetchSessionStats = async () => {
      try {
        const apiClient = getApiClient();
        const response = await apiClient.getPublicStatistics();
        
        if (response && response.statistics) {
          const stats = response.statistics;
          setSessionStats({
            count: stats.exact_count || stats.facilitated_sessions || 0,
            displayText: stats.exact_count ? stats.exact_count.toLocaleString() : stats.display_text || "0",
            isLoading: false,
            dataSource: stats.data_source,
            lastUpdated: stats.last_updated
          });
        }
      } catch (error) {
        console.error('Failed to fetch session statistics:', error);
        setSessionStats({
          count: 0,
          displayText: "0",
          isLoading: false
        });
      }
    };

    fetchSessionStats();
  }, []);

  // Fetch SLA data for generate-response endpoint
  useEffect(() => {
    const fetchSlaData = async () => {
      try {
        const apiClient = getApiClient();
        const slaResponse = await apiClient.getPublicSla('day'); // Get day period for average
        
        if (slaResponse && slaResponse.success && slaResponse.endpoints) {
          // Look for generate-response endpoint
          const generateResponseEndpoint = slaResponse.endpoints['generate-response'];
          
          if (generateResponseEndpoint && generateResponseEndpoint.average_duration_ms) {
            // Convert milliseconds to seconds
            const avgDurationSeconds = generateResponseEndpoint.average_duration_ms / 1000;
            setSlaResponseTime(avgDurationSeconds);
            logger.debug('PROOF-CONTAINER', 'SLA response time loaded', { avgDurationSeconds });
          } else {
            logger.warn('PROOF-CONTAINER', 'generate-response endpoint not found in SLA data');
            // Fallback to default 1.3s if not found
            setSlaResponseTime(1.3);
          }
        } else {
          logger.warn('PROOF-CONTAINER', 'SLA data not available, using fallback');
          // Fallback to default 1.3s if SLA fetch fails
          setSlaResponseTime(1.3);
        }
      } catch (error) {
        console.error('Failed to fetch SLA data:', error);
        // Fallback to default 1.3s on error
        setSlaResponseTime(1.3);
      }
    };

    fetchSlaData();
  }, []);

  // Animate counter values
  useEffect(() => {
    const animateValue = (start, end, duration, callback) => {
      const range = end - start;
      const steps = 100;
      const stepDuration = duration / steps;
      let current = start;
      let step = 0;

      const updateValue = () => {
        step++;
        const progress = step / steps;
        const easedProgress = 1 - Math.pow(1 - progress, 3); // Cubic ease-in
        current = Math.floor(start + (range * easedProgress));
        
        if (step < steps) {
          callback(current);
          setTimeout(updateValue, stepDuration);
        } else {
          callback(end);
        }
      };

      updateValue();
    };

    // Animate all metrics
    const timer = setTimeout(() => {
      // Use live session count for total interactions
      const sessionCount = sessionStats.count || 0;
      animateValue(0, sessionCount, 5000, (value) => {
        setAnimatedValues(prev => ({ ...prev, totalInteractions: value }));
      });
      
      animateValue(0, 285, 3000, (value) => {
        setAnimatedValues(prev => ({ ...prev, avgROI: value }));
      });
      
      animateValue(0, 72, 2500, (value) => {
        setAnimatedValues(prev => ({ ...prev, costReduction: value }));
      });
      
      // Use SLA response time if available, otherwise fallback to 1.3s
      const targetResponseTime = slaResponseTime !== null ? slaResponseTime : 1.3;
      // Animate to target value (multiply by 10 for animation precision, then divide by 10)
      const targetValue = Math.round(targetResponseTime * 10);
      animateValue(0, targetValue, 2000, (value) => {
        setAnimatedValues(prev => ({ ...prev, responseTime: value / 10 }));
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [sessionStats.count, slaResponseTime]); // Include slaResponseTime in dependencies

  // Filter case studies based on industry and search query
  const filteredCaseStudies = (caseStudies || []).filter(study => {
    if (!study) return false;
    const matchesIndustry = selectedIndustry === 'all' || (study.industry && study.industry === selectedIndustry);
    const matchesSearch = searchQuery === '' || 
      (study.company && study.company.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (study.description && study.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (study.metrics && Array.isArray(study.metrics) && study.metrics.some(metric => 
        (metric.label && metric.label.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (metric.value && metric.value.toLowerCase().includes(searchQuery.toLowerCase()))
      ));
    return matchesIndustry && matchesSearch;
  });

  // Filter integrations based on industry and search query
  const filteredIntegrations = integrations.filter(integration => {
    const matchesIndustry = selectedIndustry === 'all' || integration.industry.toLowerCase() === selectedIndustry;
    const matchesSearch = searchQuery === '' || 
      integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      integration.focus.toLowerCase().includes(searchQuery.toLowerCase()) ||
      integration.keyFeatures.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesIndustry && matchesSearch;
  });

  // Filter partners based on search query
  const filteredPartners = (partners || []).filter(partner => {
    if (!partner) return false;
    const matchesSearch = searchQuery === '' || 
      (partner.name && partner.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (partner.category && partner.category.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (partner.description && partner.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesSearch;
  });

  // Pagination logic
  const itemsPerPage = 3;
  const totalPages = Math.ceil(filteredCaseStudies.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCaseStudies = filteredCaseStudies.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedIndustry, searchQuery]);

  return (
    <div className="besper-impact-wrapper">
      <div className="impact-container">
        <ProofHeader proofT={proofT} />
        {isAuthenticated && (
          <ProofMetrics 
            sessionStats={sessionStats}
            animatedValues={animatedValues}
            proofT={proofT}
          />
        )}
        <div className="content-section">
          <div className="section-header">
            <ProofTabNavigation 
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              proofT={proofT}
              hideTechStack={hideTechStack}
              hideIntegrationsTab={hideIntegrationsTab}
            />
            <SearchFilterControls
              activeTab={activeTab}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedIndustry={selectedIndustry}
              setSelectedIndustry={setSelectedIndustry}
              proofT={proofT}
            />
          </div>

          {activeTab === 'case-studies' && (
            <CaseStudiesPanel
              caseStudies={caseStudies}
              caseStudiesLoading={caseStudiesLoading}
              filteredCaseStudies={filteredCaseStudies}
              paginatedCaseStudies={paginatedCaseStudies}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              startIndex={startIndex}
              endIndex={endIndex}
              searchQuery={searchQuery}
              proofT={proofT}
              isAuthenticated={isAuthenticated}
            />
          )}

          {activeTab === 'partnerships' && (
            <PartnersPanel
              partners={partners}
              partnersLoading={partnersLoading}
              filteredPartners={filteredPartners}
              searchQuery={searchQuery}
              proofT={proofT}
            />
          )}

          {activeTab === 'tech-stack' && !hideTechStack && (
            <TechStackPanel proofT={proofT} />
          )}

          {activeTab === 'integrations' && !hideIntegrationsTab && (
            <IntegrationsPanel
              filteredIntegrations={filteredIntegrations}
              proofT={proofT}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProofContainer;

